import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import { AlgoliaBroker } from '@fnModels/algolia/broker';
import { Employee } from '@models/company';
import { Brokerage } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BrokerSearchState } from '@state/algolia-search/broker-search';
import { BrokerSearchDispatch } from '@state/algolia-search/broker-search/broker-search.dispatch';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import {
	CreateBrokerComponent,
	CreateBrokerData,
} from './create/create-broker.component';
import { TestHooks } from './broker.hooks';

@UntilDestroy()
@Component({
	selector: 'app-broker',
	templateUrl: './broker.component.html',
	standalone: false,
})
export class BrokerComponent {
	testHooks = TestHooks;
	public brokerName = '';

	public _focus = false;
	public ICON = ICON;
	public hoverShadow = true;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	@ViewSelectSnapshot(BrokerSearchState.getList) searchResult: any;

	@Input() brokerage: Brokerage;
	@Input() orientation: 'row' | 'col' = 'col';
	@Input() label = 'Agent';
	@Input() skeletonLoading: boolean = false;

	@Output() onSelectedBroker = new EventEmitter<AlgoliaBroker>();
	@Output() onRemoveBroker = new EventEmitter<string>();
	@Output() onCreateBroker = new EventEmitter<Employee>();
	@Output() onFocus = new EventEmitter();

	constructor(
		private dialog: MatDialog,
		private brokerSearchDispatch: BrokerSearchDispatch,
	) {}

	_onFocus() {
		this.onFocus.emit();
		this._focus = true;
	}

	setBroker(ev: DropdownResult<Employee>) {
		if (ev.group === 1) {
			const result = ev.result as AlgoliaBroker;
			this.onSelectedBroker.emit(result);
		}
	}

	removeBroker(brokerId: string) {
		this.onRemoveBroker.emit(brokerId);
		this.brokerName = '';
	}

	onCreateBrokerClick(): void {
		const { brokerage, brokerName } = this;
		const names = brokerName.trim().split(' ');
		const lastName = names.shift()?.replace(/,/, '');
		const firstName = names.join(' ');
		this.createBroker({
			brokerage,
			firstName,
			lastName,
		});
	}

	createBroker(data: CreateBrokerData) {
		this.dialog
			.open<CreateBrokerComponent, CreateBrokerData, Employee>(
				CreateBrokerComponent,
				{
					width: '800px',
					height: '500px',
					data,
				},
			)
			.afterClosed()
			.pipe(untilDestroyed(this))
			.subscribe((result) => {
				if (result) {
					this._focus = false;
					this.onCreateBroker.emit(result);
				}
			});
	}

	searchBrokers(value) {
		this.brokerSearchDispatch.Search(
			value,
			this.brokerage.id,
			this.brokerage.leap?.cardId,
		);
		this.brokerName = value;
	}

	brokerDropdownOptionSelect(event) {
		switch (event.result.name) {
			case 'Edit': {
				const { brokerage } = this;
				const { firstName, lastName } = brokerage.broker;
				this.createBroker({
					brokerage,
					firstName,
					lastName,
				});
				break;
			}
			case 'Remove':
				this.removeBroker(this.brokerage.id);
				break;
		}
	}
}
